
// client/src/components/media/SchedulePost.jsx
// UNIFIED VERSION - Same platform options and logic as MediaPost, plus scheduling
// UPDATED with TimeSelect and timezone support

import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import AppFooter from "../AppFooter";
import { FaCalendarCheck, FaSpinner, FaArrowLeft, FaEdit } from "react-icons/fa";
import TimeSelect from '../TimeSelect';
import { localToUTC, getNextSlotInTenantTZ } from '../../utils/timezoneUtils';
import PlatformSelector from '../PlatformSelector';
import MediaEditor from './MediaEditor';
import SubtitleEditor from './SubtitleEditor';
import MentionInput from './MentionInput';
import MediaTagOverlay from './MediaTagOverlay';

// ---- helpers (same as MediaPost) ---------------------------------------------------------------
const isImageUrl = (u = "") => {
  const clean = String(u).split("?")[0];
  const ext = clean.includes(".") ? clean.split(".").pop().toLowerCase() : "";
  return ["jpg", "jpeg", "png", "gif", "webp", "heic", "avif"].includes(ext);
};


const detectKind = (urls = []) => {
  const first = (urls[0] || "").toLowerCase().split("?")[0];
  if (/\.(mp4|m4v)$/.test(first)) return "video";
  if (/\.(mov|qt)$/.test(first)) return "mov";
  if (/\.(jpg|jpeg|png|gif|webp|heic|avif)$/.test(first)) return "image";
  return "";
};

const isLocalPublic = (u, base) => {
  try {
    const root = String(base).replace(/\/+$/, "");
    const abs = /^https?:\/\//i.test(String(u)) ? String(u) : `${root}/${String(u).replace(/^\/+/, "")}`;
    const url = new URL(abs);
    return url.pathname.startsWith("/public/");
  } catch {
    return false;
  }
};

export default function SchedulePost() {
  const { tenant, eventId, itemId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const baseUrl =
    process.env.REACT_APP_BASE_URL ||
    window.location.origin ||
    "http://localhost:5000";

  // Normalize to absolute, publicly reachable URL (same as MediaPost)
  const toPublic = (u) => {
    if (!u) return u;
    const root = String(baseUrl).replace(/\/+$/, "");
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith("/public/")) return `${root}${u}`;
    if (u.startsWith("/tenants/")) return `${root}/public${u}`;
    const combined = `${root}/${String(u).replace(/^\/+/, "")}`;
    return encodeURI(combined);
  };


  // ---------------- incoming defaults (same as MediaPost) ----------------
  const defaultText =
    state?.content || state?.caption || state?.title || state?.description || "";

  const fromStateImages =
    (Array.isArray(state?.images) && state.images) ||
    (Array.isArray(state?.mediaUrls) && state.mediaUrls) ||
    (Array.isArray(state?.media) && state.media) ||
    (Array.isArray(state?.selectedMedia?.images) && state.selectedMedia.images) ||
    (Array.isArray(state?.media?.images) && state.media.images) ||
    [];

  const fromStateVideos =
    (Array.isArray(state?.videos) && state.videos) ||
    (Array.isArray(state?.selectedMedia?.videos) && state.selectedMedia.videos) ||
    (Array.isArray(state?.media?.videos) && state.media.videos) ||
    [];

  const unique = (arr) => Array.from(new Set((arr || []).filter(Boolean)));

  const imagesRaw = useMemo(() => unique(fromStateImages), [fromStateImages]);
  const videosRaw = useMemo(() => unique(fromStateVideos), [fromStateVideos]);

  const imagesAbsAll = useMemo(() => imagesRaw.map(toPublic), [imagesRaw]);
  const videosAbs = useMemo(() => videosRaw.map(toPublic), [videosRaw]);

  const imagesAbs = useMemo(() => imagesAbsAll.filter(isImageUrl), [imagesAbsAll]);

  // ---------------- local state (same as MediaPost + scheduling) ----------------
  const [content, setContent] = useState(defaultText);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Image editor state
  const [editingImageIdx, setEditingImageIdx] = useState(null);
  const [editedImages, setEditedImages] = useState([]);

  // User tags on media (per image index): { [imageIdx]: [{ username, x, y }] }
  const [userTags, setUserTags] = useState({});

  // Video subtitle editor state
  const [editingVideoIdx, setEditingVideoIdx] = useState(null);
  const [videoSubtitles, setVideoSubtitles] = useState([]); // VTT URLs per video index

  // Effective images: use edited replacements where available
  const effectiveImages = useMemo(() =>
    imagesAbs.map((url, i) => editedImages[i] || url),
    [imagesAbs, editedImages]
  );

  const blueskyImages = useMemo(
    () => effectiveImages.filter(isImageUrl).slice(0, 4),
    [effectiveImages]
  );

  // SAME platform toggles as MediaPost (for own tenant)
  const [platforms, setPlatforms] = useState({
    instagram: false,
    facebook: false,
    twitter: false,
    linkedin: false,
    bluesky: false,
    threads: false,
    youtube: false,
    tiktok: false,
    reddit: false,
  });

  // Multi-account posting: collaborator accounts and their platform selections
  const [collabAccounts, setCollabAccounts] = useState([]); // from API
  const [collabSelections, setCollabSelections] = useState({}); // { [tenantId]: { [platform]: bool } }

  // Reddit-specific state
  const [redditSubreddits, setRedditSubreddits] = useState([]);
  const [redditSubreddit, setRedditSubreddit] = useState('');
  const [redditTitle, setRedditTitle] = useState('');

  // TikTok Direct Post state
  const [tiktokMode, setTiktokMode] = useState('upload');
  const [tiktokTitle, setTiktokTitle] = useState(''); // Point 2a: Title field
  const [tiktokPrivacy, setTiktokPrivacy] = useState(''); // No default per TikTok guidelines
  const [tiktokDisableDuet, setTiktokDisableDuet] = useState(true); // Unchecked by default per guidelines
  const [tiktokDisableStitch, setTiktokDisableStitch] = useState(true);
  const [tiktokDisableComment, setTiktokDisableComment] = useState(true);
  const [tiktokIsDirect, setTiktokIsDirect] = useState(false);
  const [tiktokCreator, setTiktokCreator] = useState(null);
  const [tiktokCommercialToggle, setTiktokCommercialToggle] = useState(false);
  const [tiktokBrandOrganic, setTiktokBrandOrganic] = useState(false);
  const [tiktokBrandContent, setTiktokBrandContent] = useState(false);

  // Fetch Reddit subreddits when Reddit is toggled on
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    if (platforms.reddit && redditSubreddits.length === 0) {
      const tenantId = Number(tenant) || 1;
      fetch(`${baseUrl}/api/integrations/reddit/subreddits?tenantId=${tenantId}`)
        .then(r => r.json())
        .then(data => {
          if (data.success && data.items) setRedditSubreddits(data.items);
        })
        .catch(err => console.error('Failed to fetch subreddits:', err));
    }
  }, [platforms.reddit]);

  // Check TikTok status and fetch creator info when TikTok is toggled on
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useMemo(() => {
    if (platforms.tiktok) {
      const tenantId = Number(tenant) || 1;
      fetch(`${baseUrl}/api/integrations/tiktok/status?tenantId=${tenantId}`)
        .then(r => r.json())
        .then(data => {
          const hasDirect = data.connected && data.isDirect;
          setTiktokIsDirect(hasDirect);
          if (!hasDirect) setTiktokMode('upload');
          if (hasDirect) {
            fetch(`${baseUrl}/api/integrations/tiktok/creator-info?tenantId=${tenantId}`)
              .then(r => r.json())
              .then(ci => {
                if (ci.success && ci.creator) setTiktokCreator(ci.creator);
              })
              .catch(err => console.error('Failed to fetch TikTok creator info:', err));
          }
        })
        .catch(err => console.error('Failed to check TikTok status:', err));
    }
  }, [platforms.tiktok]);

  // NEW: Tenant timezone
  const [tenantTimezone, setTenantTimezone] = useState('America/Chicago');

  // NEW: Scheduling state - separate date and time
  const [scheduleDate, setScheduleDate] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  const [scheduleTime, setScheduleTime] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 15);
    // Round up to next 5-minute increment
    const minutes = d.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 5) * 5;
    if (roundedMinutes === 60) {
      d.setHours(d.getHours() + 1);
      d.setMinutes(0);
    } else {
      d.setMinutes(roundedMinutes);
    }
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${mins}`;
  });

  const selected = useMemo(
    () => Object.entries(platforms).filter(([, v]) => v).map(([k]) => k),
    [platforms]
  );

  // Fetch collaborator accounts when posting from an event
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      try {
        const res = await axios.get(`/api/collab/events/${eventId}/postable-accounts`, { withCredentials: true });
        const accounts = res?.data?.accounts || [];
        // Exclude own tenant (already handled by main platform selector)
        const others = accounts.filter(a => a.tenantId !== Number(tenant));
        setCollabAccounts(others);
      } catch (err) {
        console.error('Failed to fetch postable accounts:', err);
      }
    })();
  }, [eventId, tenant]);

  // Fetch tenant timezone on mount
  useEffect(() => {
    const fetchTenantTimezone = async () => {
      try {
        const response = await axios.get(
          `${baseUrl}/api/tenants/${tenant}/timezone`,
          { withCredentials: true }
        );
        if (response.data.timezone) {
          setTenantTimezone(response.data.timezone);
          
          // Set default times in tenant timezone
          const nextSlot = getNextSlotInTenantTZ(response.data.timezone, 15);
          setScheduleDate(nextSlot.date);
          setScheduleTime(nextSlot.time);
        }
      } catch (err) {
        console.error('Failed to fetch tenant timezone:', err);
        // Fall back to browser timezone
        const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTenantTimezone(browserTZ);
      }
    };
    
    if (tenant) {
      fetchTenantTimezone();
    }
  }, [tenant, baseUrl]);

  // ---------------- actions (same as MediaPost) ----------------
  const openBlueskyConnect = () => {
    const url = `${baseUrl}/api/integrations/bluesky/login?tenantId=${Number(tenant || 1)}`;
    window.open(url, "bsky-connect", "width=520,height=520,menubar=no,toolbar=no");
  };

  const ensureIgReady = async (tenantId) => {
    const safeFetch = async (url) => {
      try {
        const r = await fetch(url, { credentials: "include" });
        return r.ok ? r.json() : null;
      } catch {
        return null;
      }
    };
    // Check BOTH peek endpoints. FB peek lists Facebook Pages (which may or may
    // not have a linked IG account); IG _peek also includes Instagram Business
    // Login connections that don't go through a Facebook Page. Merge both so
    // either path counts as eligible.
    const [fbPeek, igPeek] = await Promise.all([
      safeFetch(`${baseUrl}/api/integrations/facebook/peek?tenantId=${tenantId}`),
      safeFetch(`${baseUrl}/api/integrations/instagram/_peek?tenantId=${tenantId}`),
    ]);
    const pages = [
      ...(Array.isArray(fbPeek?.pages) ? fbPeek.pages : []),
      ...(Array.isArray(igPeek?.pages) ? igPeek.pages : []),
    ];
    const eligible = pages.some((p) => p?.igUserId && (p?.hasPageToken || p?.pageToken));
    if (!eligible) {
      throw new Error(
        "No connected Facebook/IG account for tenant. Go to Integrations → Facebook/Instagram and connect a Page linked to your IG."
      );
    }
  };

  // Handle editor save: MediaEditor uploads internally and returns the new URL
  const handleEditorSaved = (newUrl) => {
    const publicUrl = toPublic(newUrl);
    setEditedImages(prev => {
      const copy = [...prev];
      copy[editingImageIdx] = publicUrl;
      return copy;
    });
    setEditingImageIdx(null);
  };

  // Handle subtitle editor save: stores the VTT URL for the video
  const handleSubtitleSaved = (vttUrl) => {
    const publicUrl = vttUrl ? toPublic(vttUrl) : null;
    setVideoSubtitles(prev => {
      const copy = [...prev];
      copy[editingVideoIdx] = publicUrl;
      return copy;
    });
    setEditingVideoIdx(null);
  };

  // ---------------- MAIN SUBMIT (schedule instead of immediate post) ----------------
  const submit = async () => {
    try {
      setBusy(true);
      setError("");
      setSuccessMessage("");

      // Validation: at least one platform selected
      if (!selected.length) {
        throw new Error("Select at least one platform.");
      }

      // Define tenantId from params
      const tenantId = Number(tenant) || 1;

      // Check platform authorization before scheduling
      try {
        const statusRes = await fetch(
          `${baseUrl}/api/integrations/status/${tenantId}`,
          { headers: { 'Content-Type': 'application/json' } }
        );
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          if (statusData.success) {
            const statuses = statusData.integrations || {};
            const unauthorized = selected.filter(p => !statuses[p]?.connected);
            if (unauthorized.length) {
              const names = unauthorized.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
              toast.warn(`Please connect to ${names} before scheduling.`);
              navigate(`/${tenant}/integrations`);
              return;
            }
          }
        }
      } catch (err) {
        console.warn('[SchedulePost] Could not verify platform authorization:', err.message);
      }

      // Prepare media URLs (same as MediaPost)
      const mediaUrlsForOthers = unique([...videosAbs, ...effectiveImages]);
      const detected = detectKind(mediaUrlsForOthers);
      const inferredKind = detected === "video" ? "reel" : detected === "image" ? "image" : "";

      // Instagram validations (same as MediaPost)
      if (platforms.instagram) {
        await ensureIgReady(tenantId);

        if (detected === "mov") {
          const first = mediaUrlsForOthers[0] || "";
          if (!isLocalPublic(first, baseUrl)) {
            throw new Error(
              "Instagram only accepts MP4 (H.264 + AAC). Convert your MOV to MP4 or upload it to this app (under /public) so we can auto-convert."
            );
          }
        }

        if (!mediaUrlsForOthers.length && !content.trim()) {
          throw new Error("Instagram requires at least one image/video or a caption.");
        }
      }

      // TikTok Direct Post validation
      if (platforms.tiktok && tiktokMode === 'direct') {
        // Point 1b: Block posting when creator can't post more
        if (tiktokCreator?.cannotPostReason) {
          throw new Error("You've reached TikTok's posting limit. Please try again later.");
        }
        if (!tiktokPrivacy) throw new Error("Please select a privacy level for your TikTok post.");
        if (tiktokCommercialToggle && !tiktokBrandOrganic && !tiktokBrandContent) {
          throw new Error("You need to indicate if your content promotes yourself, a third party, or both.");
        }
        // Point 1c: Check video duration against creator max
        if (videosAbs.length > 0 && tiktokCreator?.maxVideoPostDurationSec) {
          const videoEl = document.querySelector('video');
          if (videoEl && videoEl.duration > tiktokCreator.maxVideoPostDurationSec) {
            throw new Error(`Video duration (${Math.round(videoEl.duration)}s) exceeds TikTok's maximum of ${tiktokCreator.maxVideoPostDurationSec}s for your account.`);
          }
        }
      }

      // Reddit validation
      if (platforms.reddit) {
        if (!redditSubreddit) throw new Error("Please select a subreddit for your Reddit post.");
        if (!redditTitle.trim()) throw new Error("Reddit posts require a title.");
      }

      // Prepare Instagram carousel if multiple images
      const igCarousel = effectiveImages.length > 1
        ? effectiveImages.slice(0, 10).map((u) => ({ imageUrl: u }))
        : undefined;

      // Convert tenant-local date/time to UTC MySQL string
      const scheduledUtcMySqlRaw = localToUTC(scheduleDate, scheduleTime, tenantTimezone);

      // Ensure it has seconds (MySQL DATETIME format requires YYYY-MM-DD HH:mm:ss)
      const scheduledUtcMySql =
        /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(scheduledUtcMySqlRaw)
          ? `${scheduledUtcMySqlRaw}:00`
          : scheduledUtcMySqlRaw;

      // Create ISO version for validation
      const scheduledUtcIso = new Date(scheduledUtcMySql.replace(' ', 'T') + 'Z').toISOString();

      // Validate future time
      const now = new Date();
      const scheduledDateTime = new Date(scheduledUtcIso);
      
      if (scheduledDateTime <= now) {
        setError("Scheduled time must be in the future. Please select a later date/time.");
        setBusy(false);
        return;
      }

      // Build accountSelections for multi-account posting
      const accountSelections = [
        // Own tenant's selected platforms
        { tenantId, platforms: selected },
        // Collaborator tenant selections
        ...Object.entries(collabSelections)
          .map(([tid, platMap]) => ({
            tenantId: Number(tid),
            platforms: Object.entries(platMap).filter(([, v]) => v).map(([k]) => k),
          }))
          .filter(sel => sel.platforms.length > 0),
      ];

      // Build payload with both formats for backend compatibility
      const payload = {
        tenantId,
        eventId: eventId ? Number(eventId) : null,
        eventItemId: itemId ? Number(itemId) : null,
        platforms: selected,
        accountSelections,
        content,
        caption: content,
        mediaUrls: mediaUrlsForOthers,
        kind: inferredKind,
        shareToFeed: true,
        thumbOffsetSec: 0,
        carouselItems: igCarousel,

        // Backend route prefers scheduledAtLocal (MySQL-friendly)
        scheduledAtLocal: scheduledUtcMySql,
        scheduledAtIso: scheduledUtcIso,
        scheduledAt: scheduledUtcIso,

        timezone: tenantTimezone,

        // User tags on media (per-image array of { username, x, y })
        userTags: Object.keys(userTags).length > 0 ? userTags : undefined,

        // Reddit-specific fields (stored in provider_payload for the worker)
        ...(platforms.reddit && {
          redditSubreddit,
          redditTitle,
        }),

        // TikTok-specific fields (stored in provider_payload for the worker)
        ...(platforms.tiktok && {
          tiktokMode,
          tiktokTitle, // Point 2a: Title field
          ...(tiktokMode === 'direct' && {
            tiktokPrivacy,
            tiktokDisableDuet,
            tiktokDisableStitch,
            tiktokDisableComment,
            ...(tiktokCommercialToggle && {
              tiktokBrandOrganic,
              tiktokBrandContent,
            }),
          }),
        }),
      };

      console.log("Scheduling post with payload:", payload);

      // Make API call (use relative path for same-origin)
      const response = await axios.post(
        `/api/schedule/schedule`,
        payload,
        { 
          withCredentials: true, 
          timeout: 30000 
        }
      );

      console.log("Schedule response:", response.data);

      if (response.data.success) {
        // Display scheduled time in tenant timezone
        const scheduledDisplay = scheduledDateTime.toLocaleString('en-US', {
          timeZone: tenantTimezone,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        
        setSuccessMessage(
          `Post scheduled successfully for ${scheduledDisplay}! ` +
          `It will be published to ${selected.join(', ')}.`
        );
        
        // Navigate back after short delay
        setTimeout(() => {
          navigate(-1);
        }, 2000);
      } else {
        throw new Error(response.data.error || "Failed to schedule post");
      }

    } catch (e) {
      console.error("Error scheduling post:", e);
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e.message ||
        "Failed to schedule post";
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  // Disable submit when TikTok commercial toggle is on but no sub-option selected
  const tiktokCommercialIncomplete = platforms.tiktok && tiktokMode === 'direct' &&
    tiktokCommercialToggle && !tiktokBrandOrganic && !tiktokBrandContent;
  // Disable submit when TikTok creator can't post (Point 1b)
  const tiktokCannotPost = platforms.tiktok && tiktokMode === 'direct' && tiktokCreator?.cannotPostReason;

  const canSubmit =
    selected.length > 0 &&
    (content.trim().length > 0 || effectiveImages.length > 0 || videosAbs.length > 0) &&
    !tiktokCommercialIncomplete &&
    !tiktokCannotPost;

  // ---------------- render (same as MediaPost + date/time picker) ----------------
  return (
    <div className="container py-3">
      <h4 className="mb-3">Schedule Post</h4>

      {/* Integrations - Dropdown Selector (same as MediaPost) */}
      <div className="mb-3">
        <div className="form-label font-semibold mb-2">Select Platforms</div>
        <PlatformSelector
          platforms={platforms}
          setPlatforms={setPlatforms}
          onConnect={(platformKey) => {
            if (platformKey === 'bluesky') {
              openBlueskyConnect();
            }
          }}
          showConnectFor={['bluesky']}
        />
      </div>

      {/* Collaborator accounts (multi-account posting) */}
      {collabAccounts.length > 0 && (
        <div className="mb-3 p-3 rounded border">
          <div className="form-label font-semibold mb-2">Collaborator Accounts</div>
          <p className="text-sm text-neutral-500 mb-2">
            Also publish this post through collaborator platform accounts:
          </p>
          {collabAccounts.map((acct) => (
            <div key={acct.tenantId} className="mb-2 p-2 rounded bg-neutral-50 dark:bg-neutral-800">
              <div className="text-sm font-medium mb-1">{acct.tenantName}</div>
              <div className="flex flex-wrap gap-2">
                {acct.platforms.map((plat) => {
                  const checked = !!collabSelections[acct.tenantId]?.[plat];
                  return (
                    <label key={plat} className="flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          setCollabSelections((prev) => ({
                            ...prev,
                            [acct.tenantId]: {
                              ...prev[acct.tenantId],
                              [plat]: e.target.checked,
                            },
                          }));
                        }}
                      />
                      {plat.charAt(0).toUpperCase() + plat.slice(1)}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Content (with @mention autocomplete) */}
      <div className="mb-3">
        <label className="form-label">Post Content</label>
        <MentionInput
          value={content}
          onChange={setContent}
          rows={5}
          placeholder="Write your post... Type @ to mention a platform handle"
        />
        <div className="form-text">Type @ to mention platform handles (e.g. @nike, @caborojo)</div>
      </div>

      {/* Reddit-specific fields */}
      {platforms.reddit && (
        <div className="mb-3 p-3 rounded" style={{ background: '#fff4f0', border: '1px solid #FF4500' }}>
          <div className="font-semibold mb-2" style={{ color: '#FF4500' }}>Reddit Post Settings</div>
          <div className="mb-2">
            <label className="form-label small mb-1">Subreddit *</label>
            <select
              className="form-select"
              value={redditSubreddit}
              onChange={(e) => setRedditSubreddit(e.target.value)}
            >
              <option value="">Select a subreddit...</option>
              {redditSubreddits.map((sr) => (
                <option key={sr.name} value={sr.name}>r/{sr.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label small mb-1">Post Title *</label>
            <input
              type="text"
              className="form-control"
              value={redditTitle}
              onChange={(e) => setRedditTitle(e.target.value)}
              placeholder="Enter a title for your Reddit post"
              maxLength={300}
            />
            <div className="form-text">{redditTitle.length}/300 characters</div>
          </div>
        </div>
      )}

      {/* TikTok-specific fields */}
      {platforms.tiktok && (
        <div className="mb-3 p-3 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)' }}>
          <div className="font-semibold mb-2">TikTok Post Settings</div>

          {/* Post Mode */}
          <div className="mb-2">
            <label className="form-label small mb-1">Post Mode</label>
            <select
              className="form-select"
              value={tiktokMode}
              onChange={(e) => setTiktokMode(e.target.value)}
              disabled={!tiktokIsDirect}
            >
              <option value="upload">Upload to Inbox (draft for review)</option>
              <option value="direct" disabled={!tiktokIsDirect}>Direct Post (publish immediately)</option>
            </select>
            {!tiktokIsDirect && (
              <div className="form-text">Direct Post requires TikTok app approval. Currently using Upload to Inbox.</div>
            )}
          </div>

          {/* Direct Post settings - only show when direct mode is selected */}
          {tiktokMode === 'direct' && (
            <>
              {/* === POINT 1: Creator Info === */}
              {/* Point 1a: Creator nickname */}
              {tiktokCreator?.nickname && (
                <div className="flex items-center gap-2 mb-2 p-2 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)' }}>
                  {tiktokCreator.avatarUrl && (
                    <img src={tiktokCreator.avatarUrl} alt="" style={{ width: 28, height: 28, borderRadius: '50%' }} />
                  )}
                  <span className="small">Posting as <strong>@{tiktokCreator.nickname}</strong></span>
                </div>
              )}

              {/* Point 1b: Block posting when creator can't post more */}
              {tiktokCreator?.cannotPostReason && (
                <div className="alert alert-danger py-2 mb-2" style={{ fontSize: '12px' }}>
                  <strong>Cannot post right now.</strong> {tiktokCreator.cannotPostReason}. Please try again later.
                </div>
              )}

              {/* Point 5a: Content preview */}
              {(videosAbs.length > 0 || imagesAbs.length > 0) && (
                <div className="mb-2">
                  <label className="form-label small mb-1">Content Preview</label>
                  <div className="flex gap-2 p-2 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)', background: 'var(--bs-tertiary-bg, #f8f9fa)', overflowX: 'auto' }}>
                    {videosAbs.length > 0 ? (
                      <video src={videosAbs[0]} style={{ maxHeight: 120, maxWidth: '100%', borderRadius: 4 }} controls muted />
                    ) : (
                      imagesAbs.slice(0, 4).map((img, i) => (
                        <img key={i} src={img} alt="" style={{ maxHeight: 120, borderRadius: 4, objectFit: 'cover' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* === POINT 2: Post Metadata === */}
              {/* Point 2a: Title */}
              <div className="mb-2">
                <label className="form-label small mb-1">Title</label>
                <input
                  type="text"
                  className="form-control"
                  value={tiktokTitle}
                  onChange={(e) => setTiktokTitle(e.target.value)}
                  placeholder="Enter a title for your TikTok post"
                  maxLength={150}
                />
                <div className="form-text">{tiktokTitle.length}/150 characters</div>
              </div>

              {/* Point 2b: Privacy Level - no default, options from creator_info */}
              <div className="mb-2">
                <label className="form-label small mb-1">Privacy Level *</label>
                <select
                  className="form-select"
                  value={tiktokPrivacy}
                  onChange={(e) => {
                    setTiktokPrivacy(e.target.value);
                    if (e.target.value === 'SELF_ONLY' && tiktokBrandContent) {
                      setTiktokBrandContent(false);
                    }
                  }}
                  title={tiktokCommercialToggle && tiktokBrandContent ? 'Branded content visibility cannot be set to private.' : undefined}
                >
                  <option value="" disabled>Select privacy level...</option>
                  {(tiktokCreator?.privacyLevelOptions || ['PUBLIC_TO_EVERYONE', 'MUTUAL_FOLLOW_FRIENDS', 'SELF_ONLY']).map(opt => (
                    <option
                      key={opt}
                      value={opt}
                      disabled={opt === 'SELF_ONLY' && tiktokCommercialToggle && tiktokBrandContent}
                      title={opt === 'SELF_ONLY' && tiktokCommercialToggle && tiktokBrandContent ? 'Branded content visibility cannot be set to private.' : undefined}
                    >
                      {opt === 'PUBLIC_TO_EVERYONE' ? 'Public' : opt === 'MUTUAL_FOLLOW_FRIENDS' ? 'Friends Only' : opt === 'FOLLOWER_OF_CREATOR' ? 'Followers' : 'Private (Only Me)'}
                      {opt === 'SELF_ONLY' && tiktokCommercialToggle && tiktokBrandContent ? ' - unavailable for branded content' : ''}
                    </option>
                  ))}
                </select>
                {tiktokCommercialToggle && tiktokBrandContent && (
                  <div className="form-text text-yellow-500">Branded content visibility cannot be set to private.</div>
                )}
              </div>

              {/* Point 2c: Interaction Toggles - unchecked by default, grey out per creator settings */}
              <div className="mb-2">
                <label className="form-label small mb-1">Interaction Settings</label>
                <div className="flex flex-col gap-1">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="tiktok-sp-allow-comment"
                      checked={!tiktokDisableComment}
                      onChange={(e) => setTiktokDisableComment(!e.target.checked)}
                      disabled={tiktokCreator?.commentDisabled}
                    />
                    <label className="form-check-label small" htmlFor="tiktok-sp-allow-comment">
                      Allow Comments{tiktokCreator?.commentDisabled ? ' (disabled in your TikTok settings)' : ''}
                    </label>
                  </div>
                  {videosAbs.length > 0 && (
                    <>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="tiktok-sp-allow-duet"
                          checked={!tiktokDisableDuet}
                          onChange={(e) => setTiktokDisableDuet(!e.target.checked)}
                          disabled={tiktokCreator?.duetDisabled}
                        />
                        <label className="form-check-label small" htmlFor="tiktok-sp-allow-duet">
                          Allow Duet{tiktokCreator?.duetDisabled ? ' (disabled in your TikTok settings)' : ''}
                        </label>
                      </div>
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id="tiktok-sp-allow-stitch"
                          checked={!tiktokDisableStitch}
                          onChange={(e) => setTiktokDisableStitch(!e.target.checked)}
                          disabled={tiktokCreator?.stitchDisabled}
                        />
                        <label className="form-check-label small" htmlFor="tiktok-sp-allow-stitch">
                          Allow Stitch{tiktokCreator?.stitchDisabled ? ' (disabled in your TikTok settings)' : ''}
                        </label>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* === POINT 3: Content Disclosure Setting === */}
              <div className="mb-2 p-2 rounded" style={{ border: '1px solid var(--bs-border-color, #dee2e6)', background: 'var(--bs-tertiary-bg, #f8f9fa)' }}>
                <div className="font-semibold small mb-1">Content Disclosure Setting</div>
                <div className="form-check form-switch mb-1">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="tiktok-sp-commercial"
                    checked={tiktokCommercialToggle}
                    onChange={(e) => {
                      setTiktokCommercialToggle(e.target.checked);
                      if (!e.target.checked) {
                        setTiktokBrandOrganic(false);
                        setTiktokBrandContent(false);
                      }
                    }}
                  />
                  <label className="form-check-label small" htmlFor="tiktok-sp-commercial">
                    This {videosAbs.length > 0 ? 'video' : 'photo'} promotes yourself, a brand, a product, or a service
                  </label>
                </div>
                <div className="form-text">Turn this on if your content is commercial in nature.</div>

                {tiktokCommercialToggle && (
                  <div
                    className="ml-3 mt-2 p-2 rounded"
                    style={{ border: '1px dashed var(--bs-border-color, #dee2e6)' }}
                    title={!tiktokBrandOrganic && !tiktokBrandContent ? 'You need to indicate if your content promotes yourself, a third party, or both.' : undefined}
                  >
                    <div className="small font-semibold mb-1">You must select at least one of the options below:</div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tiktok-sp-brand-organic"
                        checked={tiktokBrandOrganic}
                        onChange={(e) => setTiktokBrandOrganic(e.target.checked)}
                      />
                      <label className="form-check-label small" htmlFor="tiktok-sp-brand-organic">
                        <strong>Your Brand</strong> — I am promoting myself or my own business
                      </label>
                    </div>

                    <div className="form-check mt-1">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="tiktok-sp-brand-content"
                        checked={tiktokBrandContent}
                        onChange={(e) => {
                          setTiktokBrandContent(e.target.checked);
                          if (e.target.checked && tiktokPrivacy === 'SELF_ONLY') {
                            setTiktokPrivacy('PUBLIC_TO_EVERYONE');
                          }
                        }}
                        disabled={tiktokPrivacy === 'SELF_ONLY'}
                        title={tiktokPrivacy === 'SELF_ONLY' ? 'Branded content visibility cannot be set to private.' : undefined}
                      />
                      <label className="form-check-label small" htmlFor="tiktok-sp-brand-content">
                        <strong>Branded Content</strong> — I am promoting another brand or a third party
                        {tiktokPrivacy === 'SELF_ONLY' ? ' (unavailable when privacy is Private)' : ''}
                      </label>
                    </div>

                    {/* Point 3a: Label preview based on selection */}
                    {tiktokBrandOrganic && !tiktokBrandContent && (
                      <div className="alert alert-info py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>
                        Your {videosAbs.length > 0 ? 'video' : 'photo'} will be labeled as &quot;Promotional content&quot;
                      </div>
                    )}
                    {tiktokBrandContent && (
                      <div className="alert alert-info py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>
                        Your {videosAbs.length > 0 ? 'video' : 'photo'} will be labeled as &quot;Paid partnership&quot;
                      </div>
                    )}

                    {/* Validation: at least one must be selected */}
                    {!tiktokBrandOrganic && !tiktokBrandContent && (
                      <div className="alert alert-danger py-1 mt-2 mb-0" style={{ fontSize: '12px' }}>
                        You need to indicate if your content promotes yourself, a third party, or both.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* === POINT 4: Consent declaration === */}
              <div className="alert alert-warning py-2 mb-2" style={{ fontSize: '12px' }}>
                By posting, you agree to TikTok's{' '}
                {tiktokCommercialToggle && (tiktokBrandContent) && (
                  <><a href="https://www.tiktok.com/legal/page/row/bc-policy" target="_blank" rel="noopener noreferrer">Branded Content Policy</a> and{' '}</>
                )}
                <a href="https://www.tiktok.com/legal/page/row/music-usage-confirmation" target="_blank" rel="noopener noreferrer">Music Usage Confirmation</a>.
              </div>

              {/* Point 5d: Processing notification */}
              <div className="alert alert-info py-2 mb-0" style={{ fontSize: '12px' }}>
                After publishing, it may take a few minutes for your content to be processed and visible on your TikTok profile.
              </div>
            </>
          )}
        </div>
      )}

      {/* UPDATED: Schedule Date & Time with TimeSelect */}
      <div className="mb-3 p-3 rounded" style={{ background: "#f8f9fa", color: "#212529" }}>
        <label className="form-label" style={{ color: "#212529" }}>
          <strong>Schedule Date & Time</strong>
        </label>
        <div className="row">
          <div className="col-md-6 mb-2">
            <label className="form-label small mb-1" style={{ color: "#6c757d" }}>Date</label>
            <input
              type="date"
              className="form-control"
              value={scheduleDate}
              min={new Date().toISOString().split('T')[0]}
              onChange={(e) => setScheduleDate(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label small mb-1" style={{ color: "#6c757d" }}>Time</label>
            <TimeSelect
              value={scheduleTime}
              onChange={setScheduleTime}
              tenantTimezone={tenantTimezone}
              showTimezone={true}
              id="schedule-time"
            />
          </div>
        </div>
        
        {/* Preview of scheduled time in tenant timezone */}
        {scheduleDate && scheduleTime && (
          <div className="alert alert-info mt-3 mb-0">
            📅 Post will be published on{" "}
            {(() => {
              try {
                // Convert to UTC then display in tenant timezone
                const utcMySql = localToUTC(scheduleDate, scheduleTime, tenantTimezone);
                const utcIso = new Date(utcMySql.replace(' ', 'T') + 'Z');
                
                return (
                  <strong>
                    {utcIso.toLocaleString('en-US', {
                      timeZone: tenantTimezone,
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </strong>
                );
              } catch {
                return <strong>{scheduleDate} {scheduleTime}</strong>;
              }
            })()}
          </div>
        )}
      </div>

      {/* Images (with text overlay option) */}
      <div className="mb-3">
        <div className="form-label">Images:</div>

        {effectiveImages.length === 0 ? (
          <div className="text-gray-400">No images provided.</div>
        ) : (
          <>
            <div
              className="border rounded mb-2"
              style={{
                width: "100%",
                maxWidth: "100%",
                background: "#111",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <MediaTagOverlay
                src={effectiveImages[0]}
                tags={userTags[0] || []}
                onChange={(tags) => setUserTags(prev => ({ ...prev, 0: tags }))}
                style={{ width: '100%' }}
                objectFit="contain"
                onError={(e) => (e.currentTarget.src = "/event.png")}
              />
              <button
                type="button"
                onClick={() => setEditingImageIdx(0)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.6)",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  zIndex: 3,
                }}
                title="Edit image"
              >
                <FaEdit /> Edit
              </button>
            </div>

            {effectiveImages.length > 1 && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 10,
                }}
              >
                {effectiveImages.slice(1).map((u, i) => {
                  const imgIdx = i + 1;
                  return (
                    <div
                      key={i}
                      className="border rounded overflow-hidden"
                      style={{ aspectRatio: "4 / 3", background: "#111", position: "relative" }}
                    >
                      <MediaTagOverlay
                        src={u}
                        tags={userTags[imgIdx] || []}
                        onChange={(tags) => setUserTags(prev => ({ ...prev, [imgIdx]: tags }))}
                        style={{ width: '100%', height: '100%' }}
                        onError={(e) => (e.currentTarget.src = "/event.png")}
                      />
                      <button
                        type="button"
                        onClick={() => setEditingImageIdx(imgIdx)}
                        style={{
                          position: "absolute",
                          top: 4,
                          right: 4,
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          padding: "3px 8px",
                          fontSize: 11,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          zIndex: 3,
                        }}
                        title="Edit image"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {platforms.bluesky && blueskyImages.length === 0 && !content.trim() && (
          <div className="form-text mt-2">
            Bluesky supports images (no video). Add at least one image or include text.
          </div>
        )}
      </div>

      {/* Text Overlay Editor */}
      {/* Image Editor */}
      {editingImageIdx !== null && effectiveImages[editingImageIdx] && (
        <MediaEditor
          src={effectiveImages[editingImageIdx]}
          tenantId={Number(tenant) || 1}
          eventId={eventId ? Number(eventId) : null}
          onSaved={handleEditorSaved}
          onClose={() => setEditingImageIdx(null)}
        />
      )}

      {/* Videos with subtitle support */}
      {!!videosAbs.length && (
        <div className="mb-3">
          <div className="form-label">Videos:</div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: 12,
              marginTop: 8,
            }}
          >
            {videosAbs.map((u, i) => (
              <div key={u} style={{ position: "relative" }}>
                <video src={u} controls style={{ width: "100%", borderRadius: 8 }}>
                  {videoSubtitles[i] && (
                    <track
                      kind="subtitles"
                      src={videoSubtitles[i]}
                      srcLang="en"
                      label="English"
                      default
                    />
                  )}
                </video>
                <button
                  type="button"
                  onClick={() => setEditingVideoIdx(i)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    background: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 13,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                  title="Edit subtitles"
                >
                  <FaEdit /> {videoSubtitles[i] ? 'Edit Subtitles' : 'Subtitles'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Subtitle Editor */}
      {editingVideoIdx !== null && videosAbs[editingVideoIdx] && (
        <SubtitleEditor
          src={videosAbs[editingVideoIdx]}
          tenantId={Number(tenant) || 1}
          eventId={eventId ? Number(eventId) : null}
          existingVttUrl={videoSubtitles[editingVideoIdx] || null}
          onSaved={handleSubtitleSaved}
          onClose={() => setEditingVideoIdx(null)}
        />
      )}

      {/* Error message */}
      {!!error && (
        <div className="alert alert-danger" role="alert">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Success message */}
      {!!successMessage && (
        <div className="alert alert-success" role="alert">
          <strong>Success!</strong> {successMessage}
        </div>
      )}

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)} disabled={busy}>
          <FaArrowLeft /> Back
        </button>
        <button
          type="button"
          className="btn-primary-action"
          onClick={submit}
          disabled={busy || !canSubmit}
          title={
            tiktokCommercialIncomplete
              ? "You need to indicate if your content promotes yourself, a third party, or both."
              : tiktokCannotPost
              ? "You've reached TikTok's posting limit. Please try again later."
              : undefined
          }
        >
          {busy ? <FaSpinner className="fa-spin" /> : <FaCalendarCheck />} {busy ? "Scheduling…" : "Schedule Post"}
        </button>
      </div>

      {/* Footer - SAME as MediaPost */}
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
            disabled: busy,
          },
          {
            icon: busy ? <FaSpinner className="fa-spin" /> : <FaCalendarCheck />,
            label: busy ? "Scheduling…" : "Schedule Post",
            onClick: submit,
            disabled: busy || !canSubmit,
            type: "submit",
          },
        ]}
      />
    </div>
  );
}